import React, { useState } from "react"
import { Link } from "react-router-dom"
import { Alert, Badge, Button, Card, CardBody, Modal, ModalBody, ModalHeader, Table } from "reactstrap"
import { familyName, plural, quantityLabel } from "pages/EventsManagement/components/Tabs/Logistics/labels"
import useXlsxDownload from "pages/EventsManagement/components/Tabs/Logistics/useXlsxDownload"
import { datesLabel } from "../Sheets/sheetLabels"
import { packagesLabel, placedName } from "../Stock/stockForm"
import { columnLabel, coverage, endColor, endLabel, excludedNote, lastYearLabel, monthName, nowAndMaxLabel, occupancyLabel, partialNote, sourceBadge,
  splitLabel } from "./forecastLabels"
import { amountLabel } from "./warehouseLabels"

const TBD = { name: "Balls TBD", productId: null, place: null, unit: "ball" }
// "22 balls", "−5 cans": a quantity in a row's unit.
const amountOf = (row, quantity) => amountLabel({ unit: row.unit }, quantity)

// A month's events for one product and place, or the Balls TBD (ticket 29 Q7): the forecast with its certain and estimated parts, the balance
// left at the month's end and last year's same month, then each event with where its quantity comes from.
const MonthDetail = ({ forecast, open, onClose }) => {
  const { row, index } = open
  const { month } = forecast.months[index]
  const cell = row.cells[index]
  const listed = forecast.events.filter(entry => entry.month === month).flatMap(entry => entry.items
    .filter(item => (item.product?.id ?? null) === row.productId && item.place === row.place).map(item => ({ entry, item })))
  const { lastYear } = forecast.months[index]
  const summary = [`Forecast ${amountOf(row, cell.quantity)} (${cell.certain} certain + ${cell.estimated} estimated)`,
    cell.balance != null && `balance at the end of the month ${amountOf(row, cell.balance)}`].filter(Boolean).join(" · ")
  const title = `${row.name} · ${monthName(month)}`
  return <Modal isOpen centered size="lg" toggle={onClose} labelledBy="logistics-forecast-month">
    <ModalHeader id="logistics-forecast-month" toggle={onClose}>{title}</ModalHeader>
    <ModalBody>
      <p className="small text-muted">{summary}.{lastYear.events != null && cell.lastYear != null
        && ` Last year in ${monthName(lastYear.month)}: ${plural(lastYear.events, "event")}, ${amountOf(row, cell.lastYear)} (calculated).`}</p>
      {listed.length ? <Table size="sm" className="logistics-stack mb-0">
        <thead><tr><th>Event</th><th>Source</th><th className="text-right">Quantity</th><th className="text-right">Now · max</th></tr></thead>
        <tbody>{listed.map(({ entry: { event, occupancy }, item }, index) => {
          const source = sourceBadge(item, occupancy)
          // Two lines of one product on a sheet (D21) list two items of the same event and source.
          return <tr key={`${event.id}-${item.source}-${item.shipment || ""}-${index}`}>
            <td><Link to={`/events/${event.id}/logistics`}><strong>{event.name || "Unnamed event"}</strong></Link>
              <div className="small text-muted">{`${datesLabel(event.startDate, event.endDate)} · ${event.organizer?.name || "No organizer"}`}</div></td>
            <td data-label="Source"><Badge color={source.color}>{source.label}</Badge>
              {item.source === "estimated" && occupancy && <div className="small text-muted">{occupancyLabel(occupancy)}</div>}</td>
            <td className="text-right" data-label="Quantity">{item.quantity}</td>
            <td className="text-right" data-label="Now · max">{nowAndMaxLabel(item)}</td>
          </tr>
        })}</tbody>
      </Table> : <p className="text-muted mb-0">No event counts this month.</p>}
      <p className="small text-muted mt-2 mb-0">“Now” is calculated from the current registrations; “max” is the benchmark at full capacity.</p>
    </ModalBody>
  </Modal>
}

// One month of a row: the forecast, its certain and estimated parts and the balance left at the month's end, opening its events.
const MonthCell = ({ row, index, month, onOpen }) => {
  const cell = row.cells[index]
  return <td className="text-right" data-label={columnLabel(month, index)}>
    <Button color="link" className="p-0 logistics-forecast-cell" aria-label={`${row.name} · ${monthName(month)}`} onClick={() => onOpen({ row, index })}>
      <strong>{cell.quantity || "—"}</strong>
      {splitLabel(cell) && <span className="d-block small text-muted">{splitLabel(cell)}</span>}
      {cell.balance != null && <span className={`d-block small ${endColor(row.product, cell.balance)}`}>{endLabel(cell.balance)}</span>}
    </Button>
  </td>
}

// Logistics → Warehouse, the consumption forecast (US 126–130, ticket 29): the balls, medals and diplomas that MPG sends, per place, over the
// rest of this month and the next five, grouped by family, then the Balls TBD apart, for information; with its XLSX. Each month's heading
// counts the calendar's events and compares them with the same month last year, with the balls it consumed.
export default function Forecast({ forecast }) {
  const download = useXlsxDownload()
  const [open, setOpen] = useState(null)
  const { months } = forecast
  const rows = forecast.rows.map(row => ({ ...row, name: placedName(row.product.name, row.place), productId: row.product.id, unit: row.product.unit,
    cells: row.months }))
  const families = [...new Set(rows.map(({ product }) => product.family))]
  const tbd = { ...TBD, cells: forecast.tbd.map(cell => ({ ...cell, balance: null, lastYear: null })) }
  // Last year's calculated consumption of balls in each month, over the ball products; none where no row has it.
  const lastYearBalls = months.map((_, index) => {
    const known = rows.filter(({ product }) => product.family === "balls").map(({ cells }) => cells[index].lastYear).filter(value => value != null)
    return known.length ? known.reduce((total, value) => total + value, 0) : null
  })
  const columns = months.length + 3
  const excluded = excludedNote(forecast.excluded)

  return <Card tag="section" aria-label="Consumption forecast"><CardBody>
    <div className="logistics-warehouse-head">
      <h5 className="card-title">Consumption forecast · next {months.length} months</h5>
      <Button size="sm" color="light" onClick={() => download.download("/exports/logistics/forecast", {}, "logistics-forecast.xlsx")} disabled={download.busy}>
        <i className="bx bx-download mr-1" aria-hidden="true" />{download.busy ? "Preparing XLSX…" : "Export XLSX"}
      </Button>
    </div>
    <p className="text-muted small">Unshipped sheets and supplementary shipments in preparation of the calendar's events. Per event: the admin-set quantity;
      the calculation once registrations close; until then the larger of the current calculation and the occupancy estimate. Medals and diplomas are
      exact.</p>
    {download.error && <Alert color="danger" isOpen toggle={download.dismiss}>{download.error}</Alert>}
    <Table size="sm" className="logistics-stack logistics-forecast mb-0">
      <thead><tr>
        <th>Product</th><th className="text-right">Balance</th>
        {months.map(({ month, events, lastYear }, index) => <th key={month} className="text-right">{columnLabel(month, index)}
          <div className="small text-muted">{plural(events, "event")}</div>
          <div className="small text-muted">{lastYearLabel(lastYear, lastYearBalls[index])}</div></th>)}
        <th>Coverage</th>
      </tr></thead>
      <tbody>
        {families.map(family => <React.Fragment key={family}>
          <tr className="logistics-forecast-family"><th scope="row" colSpan={columns}>{familyName(family)}</th></tr>
          {rows.filter(row => row.product.family === family).map(row => {
            const covered = coverage(row, months)
            return <tr key={`${row.productId}-${row.place}`}>
              <td><strong>{row.name}</strong>{!row.product.active && <Badge color="soft-secondary" className="ml-1">Inactive</Badge>}
                {row.product.grouping && <div className="small text-muted">{`1 ${row.product.grouping.name} = ${quantityLabel(row.product.grouping.units,
                  row.unit)}`}</div>}</td>
              <td className="text-right" data-label="Balance">{row.balance === null ? "—" : <>
                <span className={row.balance < 0 ? "text-danger font-weight-medium" : ""}>{amountOf(row, row.balance)}</span>
                {packagesLabel(row.product, row.balance) && <div className="small text-muted">{packagesLabel(row.product, row.balance)}</div>}
              </>}</td>
              {months.map(({ month }, index) => <MonthCell key={month} row={row} index={index} month={month} onOpen={setOpen} />)}
              <td data-label="Coverage">{covered ? <><Badge color={covered.color}>{covered.label}</Badge>
                {covered.detail && <div className="small text-muted">{covered.detail}</div>}</> : "—"}</td>
            </tr>
          })}
        </React.Fragment>)}
        <tr className="logistics-forecast-family"><th scope="row" colSpan={columns}>Club balls (information)</th></tr>
        <tr>
          <td><strong>{tbd.name}</strong><div className="small text-muted">in balls · the club's until the official ball is chosen</div></td>
          <td className="text-right" data-label="Balance">—</td>
          {months.map(({ month }, index) => <MonthCell key={month} row={tbd} index={index} month={month} onOpen={setOpen} />)}
          <td data-label="Coverage"><Badge color="soft-secondary">No stock</Badge></td>
        </tr>
      </tbody>
    </Table>
    <p className="small text-muted mt-2 mb-1">Cell: the forecast consumption, its certain and estimated parts, and → the balance at the month's end
      (<span className="text-danger">red</span> below 0, <span className="text-warning">amber</span> below the minimum). Click a cell for its events.</p>
    <p className="small text-muted mb-1">{partialNote(months[0])} “last year” counts the same month's events last year; a cell's detail adds their
      calculated consumption.{excluded && ` ${excluded}`}</p>
    <p className="small text-muted mb-0"><i className="bx bx-info-circle mr-1" aria-hidden="true" />The pending need (Stock and pending need) uses the
      sheets’ quantities now; the forecast is the consumption expected by the end. They are not added together.</p>
    {open && <MonthDetail forecast={forecast} open={open} onClose={() => setOpen(null)} />}
  </CardBody></Card>
}
